<template>
  <div class="tl-page">

    <!-- ── Filter Bar ── -->
    <div class="tl-filters">
      <div class="tl-filters__selects">
        <b-form-datepicker v-model="startDate" class="tl-datepicker" placeholder="Start Date"
          :date-format-options="{ year: 'numeric', month: '2-digit', day: '2-digit' }" />
        <b-form-datepicker v-model="endDate" class="tl-datepicker" placeholder="End Date"
          :date-format-options="{ year: 'numeric', month: '2-digit', day: '2-digit' }" />
        <select v-model="filters.details" class="tl-select tl-select--wide">
          <option v-for="o in detailsOptions" :key="o.value" :value="o.value">{{ o.text }}</option>
        </select>
        <select v-model="filters.entityType" class="tl-select">
          <option v-for="o in typeOptions" :key="o.value" :value="o.value">{{ o.text }}</option>
        </select>
        <select v-model="filters.action" class="tl-select">
          <option v-for="o in actionOptions" :key="o.value" :value="o.value">{{ o.text }}</option>
        </select>
        <select v-model="filters.user" class="tl-select tl-select--wide">
          <option v-for="o in userOptions" :key="o.value" :value="o.value">{{ o.text }}</option>
        </select>
      </div>
      <div class="tl-filters__actions">
        <div class="tl-search">
          <svg class="tl-search__icon" viewBox="0 0 20 20" fill="none">
            <circle cx="8.5" cy="8.5" r="5.5" stroke="#9ca3af" stroke-width="1.6" />
            <path d="M13 13l3 3" stroke="#9ca3af" stroke-width="1.6" stroke-linecap="round" />
          </svg>
          <input v-model="searchQuery" class="tl-search__input" placeholder="Search logs…" />
        </div>
        <span class="tl-count">{{ filteredAuditLogs.length }} Logs</span>
        <button class="tl-btn tl-btn--ghost" v-b-tooltip.hover title="Reset all filters" @click="resetFilters">↺
          Reset</button>
      </div>
    </div>

    <!-- ── Audit Table ── -->
    <div class="tl-table-card">
      <b-overlay :show="loading" rounded="sm" opacity="0.6" spinner-variant="warning">
        <div class="tl-table-scroll">
          <b-table class="tl-b-table" striped hover :items="filteredAuditLogs" :fields="fields"
            thead-class="tl-b-table__head" tbody-tr-class="tl-b-table__row">

            <template #cell(details)="data">
              <span v-if="data.item && data.item.entityType === 'service'">
                {{ determineDetailValue(data.item.oldValue?.item, data.item.newValue?.item) }}
              </span>
              <span v-else-if="data.item && data.item.entityType === 'tour'">
                {{ determineDetailValue(data.item.oldValue?.tourName, data.item.newValue?.tourName) }}
              </span>
              <span v-else class="tl-muted">N/A</span>
            </template>

            <template #cell(id)="data">
              <span v-if="data.item && (data.item.entityType === 'service' || data.item.entityType === 'tour')">
                {{ determineDetailValue(data.item.oldValue?.id, data.item.newValue?.id) }}
              </span>
              <span v-else class="tl-muted">N/A</span>
            </template>

            <template #cell(entityType)="data">
              <span class="tl-pill tl-pill--blue">{{ data.item.entityType }}</span>
            </template>

            <template #cell(action)="data">
              <span
                :class="['tl-pill', data.item.action === 'DELETE' ? 'tl-pill--red' : data.item.action === 'CREATE' ? 'tl-pill--green' : 'tl-pill--orange']">{{
                  data.item.action }}</span>
            </template>

            <template #cell(createdAt)="data">
              <span v-if="data.item && (data.item.entityType === 'service' || data.item.entityType === 'tour')">
                {{ formatDate(data.item.createdAt) }}
              </span>
              <span v-else class="tl-muted">N/A</span>
            </template>

            <template #cell(timestamp)="data">
              <span>{{ formatDate(data.item?.timestamp) }}</span>
            </template>

            <template #cell(view)="data">
              <button class="tl-icon-btn" @click="showBox(data.item)" title="View diff">👁</button>
            </template>

            <template #cell(restore)="data">
              <button class="tl-icon-btn tl-icon-btn--success" @click="restoreItem(data.item)"
                v-if="data.item.action === 'DELETE'" title="Restore">↩</button>
              <span v-else class="tl-muted">—</span>
            </template>
          </b-table>
        </div>
      </b-overlay>
    </div>

    <!-- ── Diff Viewer ── -->
    <div v-if="showBoxFlag" class="tl-diff-overlay" @click.self="closeBox">
      <div class="tl-diff-modal">
        <div class="tl-diff-header">
          <span>Changes</span>
          <button class="tl-diff-close" @click="closeBox">✕</button>
        </div>
        <div class="tl-diff-body">
          <div class="tl-diff-pane tl-diff-pane--old">
            <div class="tl-diff-pane__title">🟥 Old Values</div>
            <pre v-for="(value, key) in boxData.oldValue" :key="key"
              :class="['tl-diff-line', { 'tl-diff-line--changed': value.changed }]">{{ key }}: {{ value.value }}</pre>
          </div>
          <div class="tl-diff-pane tl-diff-pane--new">
            <div class="tl-diff-pane__title">🟢 New Values</div>
            <pre v-for="(value, key) in boxData.newValue" :key="key"
              :class="['tl-diff-line', { 'tl-diff-line--new': value.changed }]">{{ key }}: {{ value.value }}</pre>
          </div>
        </div>
      </div>
    </div>

  </div>
</template>

<script>
import moment from 'moment';

export default {
  data() {
    const currentDate = moment().toDate(); // Get the current date
    const startDate = moment().subtract(5, 'days').toDate(); // Get the date 5 days before

    return {
      auditLogs: [],
      fields: [
        { key: 'id', label: 'ID' },
        { key: 'details', label: 'Name' },
        { key: 'entityType', label: 'Type' },
        { key: 'action', label: 'Action' },
        { key: 'createdAt', label: 'Created At' },
        { key: 'timestamp', label: 'Updated At' },
        { key: 'users_permissions_user', label: 'User', formatter: (value) => value?.data?.attributes?.email || 'N/A' },
        { key: 'view', label: 'View' },
        { key: 'restore', label: 'Restore' },
      ],
      showBoxFlag: false,
      boxData: {
        oldValue: {},
        newValue: {}
      },
      searchQuery: '',
      filters: {
        entityType: '',
        details: '',
        action: '',
        user: ''
      },
      startDate: startDate,
      endDate: currentDate,
      loading: false,
    };
  },
  async mounted() {
    await this.filterAuditLogs();
  },

  computed: {
    sortedUniqueTypes() {
      return [...new Set(this.auditLogs.map(auditLog => auditLog.entityType))].sort();
    },
    typeOptions() {
      return [{ value: '', text: 'Type' },
      ...this.sortedUniqueTypes.map(entityType => ({ value: entityType, text: entityType }))
      ];
    },
    actionOptions() {
      return [{ value: '', text: 'Action' },
      ...Array.from(new Set(this.auditLogs.map(log => log.action)))
        .map(action => ({ value: action, text: action }))
      ];
    },
    userOptions() {
      return [{ value: '', text: 'User' },
      ...Array.from(new Set(this.auditLogs.map(log => log.users_permissions_user?.data?.attributes?.email || 'N/A')))
        .map(user => ({ value: user, text: user }))
      ];
    },
    detailsOptions() {
      return [{ value: '', text: 'Name' },
      ...Array.from(new Set(this.auditLogs.map(log => {
        if (log.entityType === 'service') {
          return log.oldValue?.item || log.newValue?.item || 'N/A';
        } else if (log.entityType === 'tour') {
          return log.oldValue?.tourName || log.newValue?.tourName || 'N/A';
        }
        return 'N/A';
      }))).map(detail => ({ value: detail, text: detail }))
      ];
    },
    filteredAuditLogs() {
      return this.auditLogs.filter(log => {
        const matchesType = !this.filters.entityType || log.entityType === this.filters.entityType;
        const matchesAction = !this.filters.action || log.action === this.filters.action;
        const matchesUser = !this.filters.user || (log.users_permissions_user?.data?.attributes?.email === this.filters.user);
        const matchesDetails = !this.filters.details ||
          (log.entityType === 'service' && (log.oldValue?.item === this.filters.details || log.newValue?.item === this.filters.details)) ||
          (log.entityType === 'tour' && (log.oldValue?.tourName === this.filters.details || log.newValue?.tourName === this.filters.details));

        // Check if any field contains the search query
        const matchesSearchQuery = !this.searchQuery ||
          (log.entityType && log.entityType.toLowerCase().includes(this.searchQuery.toLowerCase())) ||
          (log.action && log.action.toLowerCase().includes(this.searchQuery.toLowerCase())) ||
          (log.users_permissions_user?.data?.attributes?.email && log.users_permissions_user.data.attributes.email.toLowerCase().includes(this.searchQuery.toLowerCase())) ||
          (log.oldValue && JSON.stringify(log.oldValue).toLowerCase().includes(this.searchQuery.toLowerCase())) ||
          (log.newValue && JSON.stringify(log.newValue).toLowerCase().includes(this.searchQuery.toLowerCase()));

        return matchesType && matchesAction && matchesUser && matchesDetails && matchesSearchQuery;
      });
    }
  },
  watch: {
    startDate(newVal) {
      if (newVal && this.endDate) {
        this.filterAuditLogs();
      }
    },
    endDate(newVal) {
      if (newVal && this.startDate) {
        this.filterAuditLogs();
      }
    }
  },

  methods: {
    async restoreService(oldValue) {
      const serviceData = {
        ...oldValue,
        cities: oldValue.cities || [],
      };
      // Check if the service already exists
      const existingServiceResponse = await this.$xhr.get(`/api/services?filters[id]=${oldValue.id}`);
      if (existingServiceResponse.data.data.length > 0) {
        this.$bvToast.toast('Service already exists or has been restored.', {
          title: 'Error',
          variant: 'danger',
          autoHideDelay: 2000,
          solid: true
        });
        return;
      }

      const response = await this.$xhr.post('/api/services', { data: serviceData });

      if (response.status === 200) {
        this.$bvToast.toast('Service restored successfully!', {
          title: 'Success',
          variant: 'success',
          autoHideDelay: 2000,
          solid: true
        });
        await this.filterAuditLogs();
      }
    },

    async restoreTour(oldValue) {
      const tourData = {
        ...oldValue,
        cities: oldValue.cities || [],
      };
      const existingTourResponse = await this.$xhr.get(`/api/tours?filters[id]=${oldValue.id}`);
      if (existingTourResponse.data.data.length > 0) {
        this.$bvToast.toast('Tour already exists or has been restored.', {
          title: 'Error',
          variant: 'danger',
          autoHideDelay: 2000,
          solid: true
        });
        return;
      }

      const response = await this.$xhr.post('/api/tours', { data: tourData });

      if (response.status === 200) {
        this.$bvToast.toast('Tour restored successfully!', {
          title: 'Success',
          variant: 'success',
          autoHideDelay: 2000,
          solid: true
        });
        await this.filterAuditLogs();
      }
    }
    ,
    handleRestore(entityType, oldValue) {
      if (entityType === 'service') {
        this.restoreService(oldValue);
      } else if (entityType === 'tour') {
        this.restoreTour(oldValue);
      }
    },
    restoreItem(item) {
      const { entityType, oldValue } = item;
      this.handleRestore(entityType, oldValue);
    },
    async filterAuditLogs() {
      if (!this.startDate || !this.endDate) return;
      this.loading = true;
      const formattedStartDate = moment(this.startDate).format('YYYY-MM-DD');
      const formattedEndDate = moment(this.endDate).format('YYYY-MM-DD');
      
      let allLogs = [];
      let page = 1;
      let hasMore = true;

      try {
        while (hasMore) {
          const response = await this.$xhr.get(`/api/audit-logs?populate=*&filters[date][$gte]=${formattedStartDate}&filters[date][$lte]=${formattedEndDate}&pagination[page]=${page}&pagination[pageSize]=100`);
          const data = response.data.data;
          
          if (data && data.length > 0) {
            allLogs = allLogs.concat(data);
          }
          
          const meta = response.data.meta;
          if (meta && meta.pagination && meta.pagination.pageCount) {
            if (page >= meta.pagination.pageCount) {
              hasMore = false;
            } else {
              page++;
            }
          } else {
            if (!data || data.length < 100) {
              hasMore = false;
            } else {
              page++;
            }
          }
        }
      } catch (error) {
        console.error("Error fetching audit logs:", error);
      }

      this.auditLogs = allLogs.map(log => ({
        ...log.attributes,
        oldValue: log.attributes.oldValue || '{}',
        newValue: log.attributes.newValue || '{}'
      })).sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));
      this.loading = false;
    },

    handleDateChange(newDate) {
      this.inputDate = newDate;
      this.filterAuditLogs();

    },
    determineDetailValue(oldValue, newValue) {
      if (!oldValue || oldValue === 'N/A') {
        return newValue || 'N/A';
      }
      if (!newValue || newValue === 'N/A') {
        return oldValue;
      }
      return newValue;
    },
    formatDate(date) {
      const options = { year: 'numeric', month: 'long', day: 'numeric', hour: '2-digit', minute: '2-digit' };
      return new Date(date).toLocaleDateString(undefined, options);
    },
    showBox(item) {
      const oldValue = item.oldValue || {};
      const newValue = item.newValue || {};
      const differences = this.getDifferences(oldValue, newValue);

      this.boxData.oldValue = differences.oldValue;
      this.boxData.newValue = differences.newValue;
      this.showBoxFlag = true;
    },
    getDifferences(oldValue, newValue) {
      const oldDiff = {};
      const newDiff = {};

      if (typeof oldValue !== 'object' || oldValue === null) oldValue = {};
      if (typeof newValue !== 'object' || newValue === null) newValue = {};

      Object.keys({ ...oldValue, ...newValue }).forEach(key => {
        const oldVal = oldValue[key];
        const newVal = newValue[key];

        if (JSON.stringify(oldVal) !== JSON.stringify(newVal)) {
          oldDiff[key] = { value: oldVal, changed: true };
          newDiff[key] = { value: newVal, changed: true };
        } else {
          oldDiff[key] = { value: oldVal, changed: false };
          newDiff[key] = { value: newVal, changed: false };
        }
      });

      return { oldValue: oldDiff, newValue: newDiff };
    },
    closeBox() {
      this.showBoxFlag = false;
      this.boxData.oldValue = {};
      this.boxData.newValue = {};
    },
    resetFilters() {
      this.filters = {
        entityType: '',
        details: '',
        action: '',
        user: ''
      };
      this.startDate = null;
      this.endDate = null;
      this.filterAuditLogs();
    }
  }
};
</script>
<style scoped>
/* ──────────── Page ──────────── */
.tl-page {
  padding: 16px 18px;
  font-family: 'Inter', 'Segoe UI', system-ui, sans-serif;
}

/* ──────────── Filter Bar ──────────── */
.tl-filters {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 10px;
  background: #fff;
  border: 1px solid #e9ecef;
  border-radius: 10px;
  padding: 10px 16px;
  margin-bottom: 12px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, .05);
}

.tl-filters__selects {
  display: flex;
  flex-wrap: nowrap;
  gap: 7px;
  align-items: center;
}

.tl-filters__actions {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
}

.tl-datepicker {
  height: 36px !important;
  min-width: 130px;
  max-width: 150px;
  font-size: .82rem !important;
  border-radius: 8px !important;
  border: 1px solid #dee2e6 !important;
  background: #f9fafb !important;
}

:deep(.tl-datepicker .btn) {
  padding: 0 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100%;
}

:deep(.tl-datepicker label) {
  margin-bottom: 0;
  padding: 0;
  line-height: normal;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  text-align: center;
  width: 100%;
  flex: 1;
}

.tl-select {
  height: 32px;
  min-width: 130px;
  max-width: 160px;
  font-size: .8rem;
  padding: 0 8px;
  border: 1px solid #dee2e6;
  border-radius: 6px;
  background: #f9fafb;
  color: #374151;
  cursor: pointer;
  outline: none;
  transition: border-color .15s;
}

.tl-select:focus {
  border-color: #E2923D;
  box-shadow: 0 0 0 3px rgba(226, 146, 61, .15);
}

.tl-select--wide {
  min-width: 170px;
  max-width: 220px;
}

/* Search */
.tl-search {
  position: relative;
}

.tl-search__icon {
  position: absolute;
  left: 9px;
  top: 50%;
  transform: translateY(-50%);
  width: 15px;
}

.tl-search__input {
  height: 32px;
  padding: 0 10px 0 30px;
  min-width: 190px;
  font-size: .82rem;
  border: 1px solid #dee2e6;
  border-radius: 6px;
  background: #f9fafb;
  outline: none;
  transition: border-color .15s;
}

.tl-search__input:focus {
  border-color: #E2923D;
  box-shadow: 0 0 0 3px rgba(226, 146, 61, .15);
  background: #fff;
}

.tl-count {
  background: #fff3e0;
  color: #b45309;
  font-size: .75rem;
  font-weight: 700;
  padding: 3px 10px;
  border-radius: 20px;
  white-space: nowrap;
  border: 1px solid #fde68a;
}

.tl-btn {
  height: 32px;
  padding: 0 14px;
  font-size: .8rem;
  font-weight: 600;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  transition: opacity .15s;
  white-space: nowrap;
}

.tl-btn:hover {
  opacity: .88;
}

.tl-btn--ghost {
  background: #f1f3f5;
  color: #374151;
  border: 1px solid #dee2e6;
}

/* ──────────── Table Card ──────────── */
.tl-table-card {
  border: 1px solid #e9ecef;
  border-radius: 10px;
  overflow: hidden;
  box-shadow: 0 1px 4px rgba(0, 0, 0, .06);
}

.tl-table-scroll {
  overflow-x: auto;
  overflow-y: auto;
  max-height: 68vh;
}

/* Override b-table header with orange */
.tl-b-table {
  font-size: .82rem;
  margin-bottom: 0;
}

:deep(.tl-b-table__head th) {
  background: #E2923D !important;
  color: #fff !important;
  font-size: .72rem !important;
  font-weight: 700 !important;
  text-transform: uppercase !important;
  letter-spacing: .05em !important;
  border: none !important;
  position: sticky;
  top: 0;
  z-index: 2;
  padding: 10px 12px;
}

:deep(.tl-b-table__row) {
  cursor: pointer;
}

:deep(.tl-b-table__row:hover td) {
  background: #fff8f1 !important;
}

:deep(.tl-b-table td) {
  padding: 8px 12px;
  vertical-align: middle;
  border-bottom: 1px solid #f3f4f6;
  color: #374151;
}

/* Pills */
.tl-pill {
  display: inline-block;
  padding: 2px 8px;
  border-radius: 4px;
  font-size: .72rem;
  font-weight: 700;
}

.tl-pill--blue {
  background: #e0e7ff;
  color: #3730a3;
}

.tl-pill--green {
  background: #dcfce7;
  color: #166534;
}

.tl-pill--red {
  background: #fee2e2;
  color: #b91c1c;
}

.tl-pill--orange {
  background: #fff3e0;
  color: #b45309;
}

.tl-muted {
  color: #9ca3af;
}

/* Icon buttons */
.tl-icon-btn {
  background: none;
  border: 1px solid #e5e7eb;
  border-radius: 6px;
  padding: 3px 8px;
  cursor: pointer;
  font-size: 1rem;
  line-height: 1;
  transition: background .12s;
}

.tl-icon-btn:hover {
  background: #fff8f1;
  border-color: #E2923D;
}

.tl-icon-btn--success:hover {
  background: #f0fdf4;
  border-color: #16a34a;
  color: #16a34a;
}

/* ──────────── Diff Viewer ──────────── */
.tl-diff-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, .35);
  z-index: 2000;
  display: flex;
  align-items: center;
  justify-content: center;
}

.tl-diff-modal {
  background: #fff;
  border-radius: 12px;
  box-shadow: 0 20px 60px rgba(0, 0, 0, .2);
  width: min(90vw, 1100px);
  max-height: 82vh;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.tl-diff-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 14px 20px;
  background: #E2923D;
  color: #fff;
  font-weight: 700;
  font-size: .95rem;
  letter-spacing: .03em;
}

.tl-diff-close {
  background: rgba(255, 255, 255, .2);
  border: none;
  color: #fff;
  width: 28px;
  height: 28px;
  border-radius: 6px;
  cursor: pointer;
  font-size: 1rem;
  line-height: 1;
  transition: background .12s;
}

.tl-diff-close:hover {
  background: rgba(255, 255, 255, .35);
}

.tl-diff-body {
  display: flex;
  flex: 1;
  overflow: hidden;
}

.tl-diff-pane {
  flex: 1;
  padding: 16px;
  overflow-y: auto;
  font-size: .8rem;
  font-family: 'JetBrains Mono', 'Fira Mono', monospace;
}

.tl-diff-pane--old {
  background: #fafafa;
  border-right: 1px solid #e9ecef;
}

.tl-diff-pane--new {
  background: #f9fffe;
}

.tl-diff-pane__title {
  font-size: .78rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: .06em;
  color: #6b7280;
  margin-bottom: 10px;
  font-family: 'Inter', sans-serif;
}

.tl-diff-line {
  margin: 0;
  padding: 2px 6px;
  border-radius: 3px;
  white-space: pre-wrap;
  word-break: break-all;
  line-height: 1.6;
  color: #374151;
}

.tl-diff-line--changed {
  background: #fee2e2;
  color: #b91c1c;
  font-weight: 600;
}

.tl-diff-line--new {
  background: #dcfce7;
  color: #166534;
  font-weight: 600;
}
</style>
